import React from 'react';
import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import {
  FeedSample,
  NutrientEvaluation,
  REFERENCED_NUTRIENTS,
} from '../../../../services/feedAnalysisService';
import {
  formatDate,
  formatDifference,
  formatNumber,
  STATUS_STYLE,
} from '../format';

const stickyCell = {
  position: 'sticky' as const,
  left: 0,
  bg: 'gray.800',
  zIndex: 1,
};

export const StatusValue: React.FC<{ item: NutrientEvaluation }> = ({
  item,
}) => {
  const style = item.status ? STATUS_STYLE[item.status] : null;

  return (
    <Box
      as="span"
      display="inline-block"
      minW="58px"
      px="2"
      py="1"
      borderRadius="md"
      fontWeight="700"
      textAlign="center"
      bg={style?.bg || 'transparent'}
      color={style?.color || 'gray.300'}
      title={style ? style.label : undefined}
    >
      {formatNumber(item.result)}
    </Box>
  );
};

const differenceColor = (item: NutrientEvaluation) => {
  if (item.status === 'below') return 'yellow.300';
  if (item.status === 'above') return 'red.300';
  if (item.status === 'ideal') return 'green.300';
  return 'gray.500';
};

interface ResultsGridProps {
  samples: FeedSample[];
}

// grade igual à aba do mês: resultado (colorido), esperado e diferença por
// nutriente. Clicar na linha abre a amostra.
export const ResultsGrid: React.FC<ResultsGridProps> = ({ samples }) => {
  const history = useHistory();

  return (
    <Box
      bg="gray.800"
      borderWidth="1px"
      borderColor="gray.700"
      borderRadius="lg"
      overflowX="auto"
    >
      <Table size="sm">
        <Thead>
          <Tr>
            <Th rowSpan={2} {...stickyCell} minW="200px">
              Ração
            </Th>
            <Th rowSpan={2}>Amostra</Th>
            <Th rowSpan={2}>Lote</Th>
            <Th rowSpan={2}>Coleta</Th>
            {REFERENCED_NUTRIENTS.map(nutrient => (
              <Th
                key={nutrient.key}
                colSpan={3}
                textAlign="center"
                borderLeftWidth="1px"
                borderColor="gray.700"
              >
                {nutrient.label}
              </Th>
            ))}
            <Th
              rowSpan={2}
              isNumeric
              borderLeftWidth="1px"
              borderColor="gray.700"
            >
              DGM (µm)
            </Th>
            <Th rowSpan={2} isNumeric>
              DPG
            </Th>
          </Tr>
          <Tr>
            {REFERENCED_NUTRIENTS.map(nutrient => (
              <React.Fragment key={nutrient.key}>
                <Th
                  borderLeftWidth="1px"
                  borderColor="gray.700"
                  textAlign="center"
                >
                  Result.
                </Th>
                <Th isNumeric color="blue.300">
                  Esper.
                </Th>
                <Th isNumeric>Dif.</Th>
              </React.Fragment>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {samples.map(sample => (
            <Tr
              key={sample.id}
              cursor="pointer"
              _hover={{ bg: 'gray.700' }}
              onClick={() => {
                history.push(`/feed-analysis/samples/${sample.id}`);
              }}
            >
              <Td {...stickyCell} fontWeight="600">
                {sample.feed_name}
                {sample.reviewed_at && (
                  <Text fontSize="xs" color="green.300" fontWeight="500">
                    Parecer registrado
                  </Text>
                )}
              </Td>
              <Td whiteSpace="nowrap">{sample.sample_code}</Td>
              <Td whiteSpace="nowrap">{sample.lot}</Td>
              <Td whiteSpace="nowrap">{formatDate(sample.collected_on)}</Td>
              {REFERENCED_NUTRIENTS.map(nutrient => {
                const item = sample.evaluation[nutrient.key];
                return (
                  <React.Fragment key={nutrient.key}>
                    <Td
                      borderLeftWidth="1px"
                      borderColor="gray.700"
                      textAlign="center"
                    >
                      <StatusValue item={item} />
                    </Td>
                    <Td isNumeric color="blue.300">
                      {formatNumber(item.expected)}
                    </Td>
                    <Td isNumeric color={differenceColor(item)}>
                      {formatDifference(item.difference)}
                    </Td>
                  </React.Fragment>
                );
              })}
              <Td isNumeric borderLeftWidth="1px" borderColor="gray.700">
                {formatNumber(sample.dgm, 0)}
              </Td>
              <Td isNumeric>{formatNumber(sample.dpg)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
